import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { DialogHelper } from "@core/helpers/dialog-helper";
import { NotificationService } from "@core/services/notification.service";
import { TradingService } from "@core/services/trading.service";
import { ToasterSeverities } from "@enums/toaster-severities";
import { LOAD_PENDING_NAV } from "@features/dashboard/store/dashboard.actions";
import * as tradingActions from "@features/trading/store/trading.actions";
import {
  SET_NAV_HISTORY,
  SET_TRADING_LOADING_STATUS,
} from "@features/trading/store/trading.actions";
import { FundWithRelated } from "@models/fund-with-related.model";
import { Nav } from "@models/nav.model";
import { NavPeriod } from "@models/nav-period.model";
import { TradeMoment } from "@models/trade-moment-model";
import { Yieldu } from "@models/yieldu-model";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  concatMap,
  mergeMap,
  Observable,
  of,
  switchMap,
} from "rxjs";

@Injectable()
export class TradingEffects {
  loadFundList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_FUND_LIST),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: true }));
        return this.tradingService.getFundList$().pipe(
          switchMap((fundList: Array<FundWithRelated>): Observable<Action> => {
            this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: false }));
            return of(tradingActions.SET_FUND_LIST({ fundList: fundList }));
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: false }));
            return of();
          })
        );
      })
    );
  });

  loadLatestTrading$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_LATEST_TRADING),
      mergeMap((actions: { definitive: boolean }): Observable<Action> => {
        return this.tradingService.getLatestTrading$(actions.definitive).pipe(
          switchMap((tradeMoment: TradeMoment): Observable<Action> => {
            return of(
              tradingActions.SET_LATEST_TRADING({ tradeMoment: tradeMoment })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  generateOrderBook$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.GENERATE_ORDER_BOOK),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(
          tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
            tradeSectionLoading: true,
          })
        );
        return this.tradingService.generateOrderBook$().pipe(
          switchMap((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Success,
              "ORDER_BOOK_MAIL_SUCCESS"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Error,
              "ORDER_BOOK_MAIL_FAILED",
              response?.error
            );
            return of();
          })
        );
      })
    );
  });

  updateTransactions$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.UPDATE_TRANSACTIONS),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(
          tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
            tradeSectionLoading: true,
          })
        );
        return this.tradingService.updateTransactions$().pipe(
          switchMap((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Success,
              "UPDATE_TRANSACTIONS_SUCCESS"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Error,
              "UPDATE_TRANSACTIONS_FAILED",
              response?.error
            );
            return of();
          })
        );
      })
    );
  });

  loadTransactionsCount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_TRANSACTIONS_COUNT),
      concatMap((): Observable<Action> => {
        return this.tradingService.getTransactionsCount$().pipe(
          switchMap((transactions: number): Observable<Action> => {
            return of(
              tradingActions.SET_TRANSACTIONS_COUNT({
                transactions: transactions,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  postTrading$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.POST_TRADING),
      mergeMap((action: { tradingCanceled?: boolean }): Observable<Action> => {
        this.store.dispatch(
          tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
            tradeSectionLoading: true,
          })
        );
        return this.tradingService.postTrading$(action.tradingCanceled).pipe(
          switchMap((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            return of(
              tradingActions.LOAD_LATEST_TRADING({
                definitive: action.tradingCanceled
                  ? action.tradingCanceled
                  : false,
              })
            );
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            return of();
          })
        );
      })
    );
  });

  deleteTrading$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.DELETE_TRADING),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(
          tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
            tradeSectionLoading: true,
          })
        );
        return this.tradingService.deleteTrading$().pipe(
          switchMap((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Success,
              "TRADING_CANCELED"
            );
            return of(tradingActions.LOAD_LATEST_TRADING({ definitive: true }));
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            return of();
          })
        );
      })
    );
  });

  exportReport$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.EXPORT_REPORT),
      mergeMap(
        (action: {
          registerReport: boolean;
          dateStart?: Date;
          dateEnd?: Date;
        }): Observable<Action> => {
          return this.tradingService
            .getTradingReport$(
              action.registerReport,
              action.dateStart,
              action.dateEnd
            )
            .pipe(
              switchMap((): Observable<Action> => {
                if (action.registerReport) {
                  this.notificationService.show(
                    ToasterSeverities.Success,
                    "CUMULATIVE_REGISTER_BACKGROUND"
                  );
                } else {
                  this.notificationService.show(
                    ToasterSeverities.Success,
                    "COST_EXPORT_BACKGROUND"
                  );
                }
                return of();
              }),
              catchError((): Observable<Action> => {
                return of();
              })
            );
        }
      )
    );
  });

  loadNavHistory$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.GENERATE_NAV_HISTORY),
      mergeMap(
        (action: {
          selectedIds: Array<string>;
          dateStart: string | null;
          dateEnd: string | null;
        }): Observable<Action> => {
          this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: true }));
          return this.tradingService
            .getNavHistory$(
              action.selectedIds,
              action.dateStart,
              action.dateEnd
            )
            .pipe(
              switchMap((list: Array<NavPeriod>): Observable<Action> => {
                this.store.dispatch(SET_NAV_HISTORY({ navTable: list }));
                return of(
                  tradingActions.SET_TRADING_LOADING_STATUS({ loading: false })
                );
              }),
              catchError((): Observable<Action> => {
                this.store.dispatch(
                  SET_TRADING_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  savePendingNav$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.SAVE_PENDING_NAV),
      concatMap((action: { navValues: Array<Nav> }): Observable<Action> => {
        tradingActions.SET_TRADING_LOADING_STATUS({ loading: true });
        return this.tradingService.savePendingNav$(action.navValues).pipe(
          switchMap((): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "COMMITED_FOR_APPROVAL"
            );
            this.store.dispatch(LOAD_PENDING_NAV());
            return of(tradingActions.LOAD_FUND_LIST());
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            tradingActions.SET_TRADING_LOADING_STATUS({ loading: false });
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_COULD_NOT_BE_SAVED",
              response?.error
            );
            return of();
          })
        );
      })
    );
  });

  loadYielduEfficiency$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_YIELDU_EFFICIENCY),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: true }));
        return this.tradingService.getYielduEfficiency$().pipe(
          switchMap((list: Array<Yieldu>): Observable<Action> => {
            this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: false }));
            return of(
              tradingActions.SET_YIELDU_EFFICIENCY({ yielduList: list })
            );
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: false }));
            return of();
          })
        );
      })
    );
  });

  saveYielduEfficiency$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.SAVE_YIELDU_EFFICIENCY),
      concatMap((action: { savedData: Array<Yieldu> }): Observable<Action> => {
        return this.tradingService.saveYielduEfficiency$(action.savedData).pipe(
          switchMap((): Observable<Action> => {
            this.dialog.closeAll();
            this.notificationService.show(
              ToasterSeverities.Success,
              "YIELDU_COMITTED_FOR_CONFIRM"
            );
            return of(tradingActions.LOAD_YIELDU_EFFICIENCY());
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_COULD_NOT_BE_SAVED",
              response?.error
            );
            return of();
          })
        );
      })
    );
  });

  loadTradeStatus$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_TRADE_STATUS),
      concatMap((): Observable<Action> => {
        return this.tradingService.checkTradeStatus$().pipe(
          switchMap((tradeStatus: boolean): Observable<Action> => {
            return of(tradingActions.SET_TRADE_STATUS({ tradeStatus }));
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private tradingService: TradingService,
    private store: Store,
    private notificationService: NotificationService,
    private dialog: DialogHelper
  ) {}
}
